import React, { useState, useEffect, useCallback } from 'react';
import api, { getCheckInChartData } from '../../services/api'; // Use the default export and named export

// MUI Imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip'; // Import Chip for status display

// Recharts Imports
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, Cell // Added BarChart components
} from 'recharts';


function ProgressDashboard({ token }) { // Assume token is passed as prop
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [templateChartData, setTemplateChartData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) {
      setError('Authentication token not found.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await api.getProgressData(token);
      setDashboardData(data);
      console.log("Dashboard Data:", data); // For debugging

      // Fetch template chart data from the dedicated API endpoint (for client's own data, no clientId needed)
      try {
        const chartResponse = await getCheckInChartData(null, null, 90, token);
        if (chartResponse?.success && chartResponse?.series?.length > 0) {
          setTemplateChartData(chartResponse);
          console.log('[ProgressDashboard] Template chart data loaded:', {
            templateName: chartResponse.template?.name,
            seriesCount: chartResponse.series?.length,
            totalCheckIns: chartResponse.totalCheckIns
          });
        }
      } catch (chartErr) {
        console.log('[ProgressDashboard] Template chart API not available, using legacy format:', chartErr.message);
      }
    } catch (err) {
      console.error("Error fetching progress data:", err);
      // Log the full error object for more details
      console.error("Full error object:", err);
      // Check if the error response has more specific details
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load progress data.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper to extract value from dynamicResponses by fieldId
  const getResponseValue = (checkIn, fieldIds) => {
    // First check top-level fields
    for (const fieldId of fieldIds) {
      if (checkIn[fieldId] != null) return checkIn[fieldId];
    }
    // Then check dynamicResponses
    if (checkIn.dynamicResponses) {
      for (const fieldId of fieldIds) {
        const response = checkIn.dynamicResponses.find(r => r.fieldId === fieldId);
        if (response?.value != null) return response.value;
      }
    }
    return null;
  };

  // Helper function to format legacy check-in data for charts
  const formatCheckInDataForChart = (checkIns) => {
    if (!checkIns) return [];
    // Determine if we have mostly template-based or legacy check-ins
    const templateCheckIns = checkIns.filter(c => c.dynamicResponses?.length > 0 || c.sudsRating != null);
    const legacyCheckIns = checkIns.filter(c => c.moodRating != null);

    if (templateCheckIns.length > legacyCheckIns.length) {
      // Template-based: show SUDS and Presence (check both top-level and dynamicResponses)
      return checkIns
        .filter(c => {
          const suds = getResponseValue(c, ['sudsRating', 'suds']);
          const presence = getResponseValue(c, ['presenceRating', 'presence']);
          return suds != null || presence != null;
        })
        .map(entry => ({
          date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          SUDS: getResponseValue(entry, ['sudsRating', 'suds']) ?? 0,
          Presence: getResponseValue(entry, ['presenceRating', 'presence']) ?? 0,
        }));
    } else {
      // Legacy: show Mood and Anxiety
      return checkIns
        .filter(c => c.moodRating != null || c.anxietyRating != null)
        .map(entry => ({
          date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          Mood: entry.moodRating,
          Anxiety: entry.anxietyRating,
        }));
    }
  };

  // Transform template API series data to Recharts row format
  const getTemplateChartRows = () => {
    if (!templateChartData?.series?.length) return [];

    // Collect all unique dates across all series
    const dateMap = new Map();

    for (const series of templateChartData.series) {
      for (const point of series.data) {
        const dateStr = new Date(point.x).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, { date: dateStr, _sortDate: new Date(point.x) });
        }
        // Use series name as the key for this data point
        dateMap.get(dateStr)[series.name] = point.y;
      }
    }

    // Sort by date, then remove _sortDate helper property
    return Array.from(dateMap.values())
      .sort((a, b) => a._sortDate - b._sortDate)
      .map(({ _sortDate, ...rest }) => rest);
  };

  // Get chart type
  const getChartType = () => {
    if (templateChartData?.series?.length > 0) return 'template-api';
    if (!dashboardData?.checkIns) return 'legacy';
    const templateCheckIns = dashboardData.checkIns.filter(c => c.dynamicResponses?.length > 0 || c.sudsRating != null);
    const legacyCheckIns = dashboardData.checkIns.filter(c => c.moodRating != null);
    return templateCheckIns.length > legacyCheckIns.length ? 'template' : 'legacy';
  };

  // Get Y-axis config based on chart type
  const getYAxisConfig = () => {
    if (templateChartData?.template?.visualization) {
      const viz = templateChartData.template.visualization;
      return {
        domain: [viz.yAxisMin ?? 0, viz.yAxisMax ?? 10],
        ticks: viz.yAxisMax === 10 ? [0, 2, 4, 6, 8, 10] : [0, 1, 2, 3, 4, 5]
      };
    }
    const chartType = getChartType();
    return chartType === 'template' || chartType === 'template-api'
      ? { domain: [0, 10], ticks: [0, 2, 4, 6, 8, 10] }
      : { domain: [1, 5], ticks: [1, 2, 3, 4, 5] };
  };

  // Rendering chart with template support
  const renderCheckInChart = () => {
     const chartType = getChartType();
     const yAxisConfig = getYAxisConfig();

     // Use template API data if available, otherwise fall back to legacy format
     const chartData = chartType === 'template-api'
       ? getTemplateChartRows()
       : formatCheckInDataForChart(dashboardData?.checkIns);

     console.log('[ProgressDashboard] Chart debug:', JSON.stringify({
       chartType,
       checkInsCount: dashboardData?.checkIns?.length,
       chartDataLength: chartData.length,
       chartDataSample: chartData.slice(0, 3),
       templateSeriesCount: templateChartData?.series?.length,
       templateSeries: templateChartData?.series?.map(s => ({ name: s.name, dataPoints: s.data?.length }))
     }, null, 2));

     if (chartData.length === 0) {
       return <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>No check-in data available for charts.</Typography>;
     }

     // Determine chart title
     const chartTitle = chartType === 'template-api' && templateChartData?.template?.name
       ? `${templateChartData.template.name} Trends`
       : chartType === 'template' ? 'SUDS & Presence Trends' : 'Mood & Anxiety Trends';

     // Build lines array based on chart type
     const renderLines = () => {
       if (chartType === 'template-api' && templateChartData?.series) {
         const colors = ['#EF5350', '#66BB6A', '#42A5F5', '#FF8C00', '#9C27B0'];
         return templateChartData.series.map((series, idx) => {
           const color = series.color || colors[idx % colors.length];
           return (
             <Line
               key={series.name}
               type="monotone"
               dataKey={series.name}
               stroke={color}
               strokeWidth={2}
               dot={{ r: 6, fill: color, stroke: '#fff', strokeWidth: 2 }}
               activeDot={{ r: 8 }}
               connectNulls
             />
           );
         });
       }
       if (chartType === 'template') {
         return [
           <Line key="suds" type="monotone" dataKey="SUDS" stroke="#EF5350" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} connectNulls />,
           <Line key="presence" type="monotone" dataKey="Presence" stroke="#66BB6A" strokeWidth={2} dot={{ r: 4 }} connectNulls />
         ];
       }
       return [
         <Line key="mood" type="monotone" dataKey="Mood" stroke="#8884d8" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} />,
         <Line key="anxiety" type="monotone" dataKey="Anxiety" stroke="#82ca9d" strokeWidth={2} dot={{ r: 4 }} />
       ];
     };

     return (
        <Box sx={{ mt: 2 }}>
             <Typography variant="subtitle1" gutterBottom>{chartTitle}</Typography>
             <Paper variant="outlined" sx={{ p: 1, overflow: 'auto' }}>
                <LineChart width={500} height={280} data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={yAxisConfig.domain} ticks={yAxisConfig.ticks} allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    {renderLines()}
                </LineChart>
             </Paper>
        </Box>
     );
  };

  // Helper to get chip color based on status
  const getStatusChipColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'partially_completed': return 'warning';
      case 'started': return 'info';
      case 'worked_on': return 'info';
      case 'missed': return 'error';
      case 'skipped': return 'default';
      default: return 'default';
    }
  };

   // Renders the habit tracking section
  const renderHabitTracking = () => {
    if (!dashboardData?.habitTracking || dashboardData.habitTracking.length === 0) {
      return <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>No habit tracking data available.</Typography>;
    }
    // Group entries by goal
    const groupedHabits = dashboardData.habitTracking.reduce((acc, entry) => {
        const goalId = entry.relatedGoal?._id;
        if (!goalId) return acc; // Skip if goal somehow wasn't populated
        if (!acc[goalId]) {
            acc[goalId] = {
                description: entry.relatedGoal.description,
                entries: []
            };
        }
        acc[goalId].entries.push({
            date: new Date(entry.date || entry.createdAt).toLocaleDateString('en-CA'),
            status: entry.completionStatus,
            effort: entry.effortRating
        });
        return acc;
    }, {});

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Habit Goal Progress</Typography>
            {Object.entries(groupedHabits).map(([goalId, data]) => (
                 <Paper key={goalId} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                     <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{data.description}</Typography>
                     <Typography variant="caption">({data.entries.length} tracked days)</Typography>
                     {/* Simple Bar Chart for Completion Status */}
                     <Box sx={{ height: 100, mt: 1.5 }}>
                         <ResponsiveContainer width="100%" height="100%">
                             <BarChart data={[{ name: 'Status', ...data.entries.reduce((acc, e) => { acc[e.status] = (acc[e.status] || 0) + 1; return acc; }, {}) }]} layout="vertical">
                                 <XAxis type="number" hide />
                                 <YAxis type="category" dataKey="name" hide/>
                                 <Tooltip />
                                 <Legend />
                                 <Bar dataKey="Completed" stackId="a" fill="#82ca9d" />
                                 <Bar dataKey="Partially Completed" stackId="a" fill="#ffc658" />
                                 <Bar dataKey="Missed" stackId="a" fill="#ff8042" />
                                 <Bar dataKey="Skipped" stackId="a" fill="#8884d8" />
                             </BarChart>
                         </ResponsiveContainer>
                     </Box>
                     {/* Optional: List recent entries */}
                     {/* <List dense disablePadding sx={{ mt: 1 }}>
                         {data.entries.slice(-3).map((e, i) => <ListItem key={i} dense disablePadding><ListItemText primary={`${e.date}: ${e.status || 'Tracked'} (Effort: ${e.effort || 'N/A'})`} /></ListItem>)}
                         {data.entries.length > 3 && <ListItem dense disablePadding><ListItemText primary="..." /></ListItem>}
                     </List> */}
                 </Paper>
            ))}
        </Box>
    );
  };

  // Placeholder for rendering goal overview
  const renderGoalOverview = () => {
     if (!dashboardData?.goals) return null;
     const { stats } = dashboardData;
     return (
         <Box sx={{ mt: 2 }}>
             <Typography variant="subtitle1" gutterBottom>Goal Overview</Typography>
             <Paper variant="outlined" sx={{ p: 2 }}>
                 <Typography>Total Goals: {stats?.totalGoals ?? 0} (Active: {stats?.activeGoals ?? 0}, Completed: {stats?.completedGoals ?? 0})</Typography>
                 <Divider sx={{ my: 1 }}/>
                 <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Active Goals:</Typography>
                 {dashboardData.goals && dashboardData.goals.filter(g => g.status === 'In Progress').length > 0 ? (
                    <List dense disablePadding>
                        {dashboardData.goals.filter(g => g.status === 'In Progress').map(goal => (
                            <ListItem key={goal._id} dense disablePadding>
                                <ListItemText primary={goal.description} secondary={`Target: ${goal.targetDate ? new Date(goal.targetDate).toLocaleDateString() : 'N/A'}`} />
                            </ListItem>
                        ))}
                    </List>
                 ) : (
                    <Typography variant="caption" color="text.secondary">No active goals.</Typography>
                 )}
             </Paper>
         </Box>
     );
  };

  // Renders the task tracking section
  const renderTaskTracking = () => {
    if (!dashboardData?.taskTracking || dashboardData.taskTracking.length === 0) {
      return <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>No recent task activity logged.</Typography>;
    }
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Recent Task Activity</Typography>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <List dense disablePadding>
            {dashboardData.taskTracking.map((entry, index) => (
              <ListItem key={entry._id || index} dense disablePadding sx={{ mb: 0.5 }}>
                <ListItemText
                  primary={entry.relatedGoal?.description || 'Task'}
                  secondary={`Logged on: ${new Date(entry.date || entry.createdAt).toLocaleDateString()}`}
                />
                <Chip
                  label={entry.completionStatus.replace('_', ' ')} // Make status readable
                  size="small"
                  color={getStatusChipColor(entry.completionStatus)}
                  variant="outlined"
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
    );
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!dashboardData) {
    return <Typography sx={{ textAlign: 'center', p: 3 }}>No progress data available.</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>My Progress Dashboard</Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3}>
        {/* Check-in Chart */}
        <Grid size={{ xs: 12, md: 7 }}>
           {renderCheckInChart()}
        </Grid>

         {/* Stats & Goals */}
        <Grid size={{ xs: 12, md: 5 }}>
            {renderGoalOverview()}
            {/* Add other stats here if needed */}
        </Grid>

         {/* Habit Tracking */}
         <Grid size={{ xs: 12, md: 6 }}>
             {renderHabitTracking()}
         </Grid>

         {/* Task Tracking */}
         <Grid size={{ xs: 12, md: 6 }}>
             {renderTaskTracking()}
         </Grid>
      </Grid>
    </Box>
  );
}

export default ProgressDashboard;
